import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown, Loader2 } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface AsyncSearchableSelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => Promise<Option[]>;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  debounceMs?: number;
  minSearchLength?: number;
  selectedOptionLabel?: string; // Optional: pre-populated label for selected value
  onOptionSelect?: (option: Option | null) => void;
  /** Hide clear button and chevron — for inline edit contexts where parent provides save/cancel. */
  compact?: boolean;
}

export const AsyncSearchableSelect: React.FC<AsyncSearchableSelectProps> = ({
  label,
  name,
  value,
  onChange,
  onSearch,
  placeholder = 'Search...',
  required = false,
  error,
  disabled = false,
  debounceMs = 300,
  minSearchLength = 0,
  selectedOptionLabel,
  onOptionSelect,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectedLabel, setSelectedLabel] = useState<string>(selectedOptionLabel || '');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Update selected label when value or selectedOptionLabel changes
  useEffect(() => {
    if (selectedOptionLabel) {
      setSelectedLabel(selectedOptionLabel);
    } else if (value && options.length > 0) {
      const option = options.find(opt => opt.value === value);
      if (option) {
        setSelectedLabel(option.label);
      }
    } else if (!value) {
      setSelectedLabel('');
    }
  }, [value, selectedOptionLabel, options]);

  // Debounced search function
  const performSearch = useCallback(async (query: string) => {
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    if (query.length < minSearchLength) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await onSearch(query);
      // Check if request was aborted
      if (!abortControllerRef.current.signal.aborted) {
        setOptions(results);
        setHighlightedIndex(0);
      }
    } catch (error: any) {
      // Ignore abort errors
      if (error.name !== 'AbortError') {
        console.error('[AsyncSearchableSelect] Search error:', error);
        setOptions([]);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [onSearch, minSearchLength]);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (isOpen && searchTerm.length >= minSearchLength) {
      debounceTimerRef.current = setTimeout(() => {
        performSearch(searchTerm);
      }, debounceMs);
    } else if (searchTerm.length < minSearchLength) {
      setOptions([]);
      setIsLoading(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, isOpen, debounceMs, minSearchLength, performSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [options]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        // Trigger initial search if we have a search term
        if (searchTerm.length >= minSearchLength) {
          performSearch(searchTerm);
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (options[highlightedIndex]) {
          handleSelect(options[highlightedIndex].value, options[highlightedIndex].label);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleSelect = (optionValue: string, optionLabel: string) => {
    onChange(optionValue);
    onOptionSelect?.({ value: optionValue, label: optionLabel });
    setSelectedLabel(optionLabel);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    onOptionSelect?.(null);
    setSelectedLabel('');
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 0);
      // Trigger search if we have a search term
      if (searchTerm.length >= minSearchLength) {
        performSearch(searchTerm);
      }
    }
  };

  return (
    <div className={label ? 'space-y-1' : ''} ref={containerRef}>
      {label ? (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      ) : null}

      <div className="relative">
        {/* Selected value display / Search input */}
        <div
          onClick={handleOpen}
          className={`
            w-full px-3 py-2
            bg-white dark:bg-[#0f172a]
            border rounded-lg
            text-gray-900 dark:text-white
            cursor-pointer
            flex items-center justify-between
            ${error
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-300 dark:border-gray-600'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'}
          `}
        >
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1 bg-transparent outline-none"
              disabled={disabled}
            />
          ) : (
            <span className={selectedLabel ? '' : 'text-gray-400 dark:text-gray-500'}>
              {selectedLabel || `Select ${label}`}
            </span>
          )}

          <div className="flex items-center gap-2">
            {isLoading && (
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            )}
            {!compact && selectedLabel && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {!compact && (
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            )}
          </div>
        </div>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl ring-1 ring-black/5 max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="w-8 h-8 mx-auto mb-2 text-gray-400 animate-spin" />
                <p>Searching...</p>
              </div>
            ) : searchTerm.length < minSearchLength ? (
              <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>Type at least {minSearchLength} character{minSearchLength !== 1 ? 's' : ''} to search</p>
              </div>
            ) : options.length > 0 ? (
              <ul className="py-1">
                {options.map((option, index) => (
                  <li
                    key={option.value}
                    onClick={() => handleSelect(option.value, option.label)}
                    className={`
                      px-3 py-2 cursor-pointer
                      ${index === highlightedIndex
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#334155]'
                      }
                    `}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.label}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>No results found</p>
                {searchTerm && (
                  <p className="mt-1">Try searching for something else</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-500 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};
